import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const Users = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'employee' });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('https://mevteck.com/invoice-api/auth/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await axios.post('https://mevteck.com/invoice-api/auth/register', form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('User created successfully!');
      setForm({ name: '', email: '', password: '', role: 'employee' });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`https://mevteck.com/invoice-api/auth/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Manage Users</h2>
        <button style={styles.backBtn} onClick={() => navigate('/dashboard')}>← Back</button>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.card}>
        <div style={styles.sectionTitle}>Add New User</div>
        <form onSubmit={handleSubmit}>
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Name</label>
              <input style={styles.input} value={form.name}
                onChange={e => setForm({...form, name: e.target.value})}
                placeholder="Yaseen Ansari" required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input style={styles.input} type="email" value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                placeholder="yaseen@example.com" required />
            </div>
          </div>
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <input style={styles.input} type="password" value={form.password}
                onChange={e => setForm({...form, password: e.target.value})}
                placeholder="••••••••" required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Role</label>
              <select style={styles.input} value={form.role}
                onChange={e => setForm({...form, role: e.target.value})}>
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <button style={styles.btn} type="submit">Add User</button>
        </form>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>All Users</div>
        {loading ? (
          <p style={{ color: '#888' }}>Loading...</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {['Name', 'Email', 'Role', 'Joined', ''].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u._id} style={styles.tr}>
                  <td style={styles.td}>{u.name}</td>
                  <td style={styles.td}>{u.email}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      background: u.role === 'admin' ? '#1a2a3a' : '#1a1a2a',
                      color: u.role === 'admin' ? '#4da6ff' : '#aaa'
                    }}>{u.role}</span>
                  </td>
                  <td style={styles.td}>{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td style={styles.td}>
                    {u._id !== user?.id && (
                      <button style={styles.delBtn} onClick={() => deleteUser(u._id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { margin: '0 auto', padding: '2rem', background: '#0f0f0f', minHeight: '100vh' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  title: { fontSize: '22px', fontWeight: '700', color: '#fff' },
  backBtn: { background: 'none', border: '1px solid #333', color: '#aaa', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  error: { background: '#2a1a1a', color: '#ff6b6b', padding: '10px', borderRadius: '8px', marginBottom: '1rem', fontSize: '14px' },
  success: { background: '#1a2a1a', color: '#4caf50', padding: '10px', borderRadius: '8px', marginBottom: '1rem', fontSize: '14px' },
  card: { background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '1.5rem', marginBottom: '1rem' },
  sectionTitle: { fontSize: '13px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' },
  field: {},
  label: { display: 'block', color: '#aaa', fontSize: '13px', marginBottom: '6px' },
  input: { width: '100%', padding: '9px 12px', background: '#111', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '14px', boxSizing: 'border-box' },
  btn: { padding: '10px 24px', background: '#fff', color: '#000', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: '12px', color: '#888', padding: '10px 8px', borderBottom: '1px solid #2a2a2a' },
  tr: { borderBottom: '1px solid #1f1f1f' },
  td: { padding: '12px 8px', fontSize: '14px', color: '#ccc' },
  badge: { padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  delBtn: { padding: '4px 10px', background: '#2a1a1a', color: '#ff6b6b', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }
};

export default Users;